import { VideoCameraIcon } from '@heroicons/react/24/outline'
import {
  ClassroomSessionItem,
  formatDateTimeRange,
  isSessionCompleted,
  resolveMeetLink,
  sessionDisplayName,
} from './types'

interface SessionScheduleGridProps {
  sessions: ClassroomSessionItem[]
  classroomMeetLink?: string | null
  loading?: boolean
}

const SessionScheduleGrid = ({
  sessions,
  classroomMeetLink,
  loading,
}: SessionScheduleGridProps) => {
  if (loading) {
    return (
      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm">
        <h3 className="text-xl font-bold text-m2-on-surface mb-4">Lịch buổi học</h3>
        <p className="text-m2-on-surface-variant">Đang tải lịch học...</p>
      </div>
    )
  }

  if (!sessions.length) {
    return (
      <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm">
        <h3 className="text-xl font-bold text-m2-on-surface mb-4">Lịch buổi học</h3>
        <p className="text-m2-on-surface-variant">Chưa có buổi học nào được lên lịch.</p>
      </div>
    )
  }

  // Giữ thứ tự lịch gốc (API đã ORDER BY start_time ASC)
  const ordered = [...sessions].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  )

  return (
    <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-6 shadow-sm">
      <h3 className="text-xl font-bold text-m2-on-surface mb-5">Lịch buổi học</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ordered.map((session) => {
          const done = isSessionCompleted(session)
          const isHighlight =
            session.displayStatus === 'UPCOMING_SOON' ||
            session.displayStatus === 'IN_PROGRESS'
          const meetLink = resolveMeetLink(session, classroomMeetLink)
          const showMeet = !done && !!meetLink

          return (
            <div
              key={session.id}
              className={`rounded-xl border p-4 flex flex-col min-h-[140px] transition ${
                done
                  ? 'border-m2-outline-variant bg-m2-surface-high text-m2-on-surface-variant'
                  : isHighlight
                    ? 'border-m2-primary/40 bg-m2-primary/10 ring-1 ring-m2-primary/40'
                    : 'border-m2-primary/40 bg-m2-primary/10'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4
                  className={`font-semibold text-[15px] leading-snug line-clamp-2 ${
                    done ? 'text-m2-on-surface-variant' : 'text-m2-on-surface'
                  }`}
                >
                  {sessionDisplayName(session)}
                </h4>
                <span
                  className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                    done
                      ? 'bg-m2-surface-container text-m2-on-surface-variant border border-m2-outline-variant'
                      : 'bg-m2-primary/20 text-m2-primary border border-m2-primary/30'
                  }`}
                >
                  {done ? 'Đã học' : 'Chưa học'}
                </span>
              </div>
              <p className="text-sm text-m2-on-surface-variant mb-auto">
                {formatDateTimeRange(session.startTime, session.endTime)}
              </p>
              {showMeet && (
                <a
                  href={meetLink!}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-semibold m2-cta-gradient text-white py-2 px-3 rounded-lg"
                >
                  <VideoCameraIcon className="h-4 w-4" />
                  Vào Meeting
                </a>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default SessionScheduleGrid
